import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { COLOR_BLACK_PERL, FONT_SIZE_13_PX, FONT_SIZE_16_PX } from 'popTheme';
import { getTimeIcon, selectIconByCategory, selectIconByMediaType } from './ResourceIcons';
import { getCategoryDisplayLabel } from './ResourceCategory';
import { sendUpdateResourceActivity } from '../../redux/sagas/updateResourceActivitySaga';
import { selectResourceData } from '../../redux/reducers/resourceDataReducer';
import { sendClientAnalytics } from '../../redux/sagas/clientAnalyticsSaga';
import { AuditEventAction, createClientAnalytics } from '../../ClientAnalytics';

export const ResourceCardExpanded = ({ id, color }) => {
  const theme = useTheme();
  const intl = useIntl();
  const dispatch = useDispatch();

  theme.typography.h5 = {
    fontSize: '1.25rem',
    [theme.breakpoints.down('lg')]: {
      fontSize: FONT_SIZE_16_PX,
    },
  };
  theme.typography.body1 = {
    fontSize: FONT_SIZE_16_PX,
    lineHeight: '1.5rem',
    [theme.breakpoints.down('lg')]: {
      fontSize: FONT_SIZE_13_PX,
      lineHeight: '1.219rem',
    },
  };

  const messages = defineMessages({
    resourceCardExpandedTaglineTitle: {
      id: 'resource.card.expanded.tagline.title',
      defaultMessage: 'What is it?',
      description: 'Tagline title for the expanded resource card',
    },
    resourceCardExpandedDescriptionTitle: {
      id: 'resource.card.expanded.description.title',
      defaultMessage: 'How does it help',
      description: 'Description title for the expanded resource card',
    },
    resourceCardExpandedLikeLabel: {
      id: 'resource.card.expanded.like.label',
      defaultMessage: 'Like',
      description: 'Label for the Like option',
    },
    resourceCardExpandedLikedLabel: {
      id: 'resource.card.expanded.liked.label',
      defaultMessage: 'Liked',
      description: 'Label for the Liked option',
    },
    resourceCardExpandedCompleteLabel: {
      id: 'resource.card.expanded.complete.label',
      defaultMessage: 'Complete',
      description: 'Label for the Complete option',
    },
    resourceCardExpandedCompletedLabel: {
      id: 'resource.card.expanded.completed.label',
      defaultMessage: 'Completed',
      description: 'Label for the Completed option',
    },
  });

  const getResourceById = (resourceId, items) => {
    return items ? items.find((item) => item.id === resourceId) || {} : {};
  };

  const resourceData = useSelector(selectResourceData);
  const data = getResourceById(id, resourceData.resources);
  const headerImage = `data:image/jpeg;base64,${data.headerImage}`;

  const [liked, setLiked] = useState(data.isLiked);
  const [completed, setCompleted] = useState(data.isCompleted);

  const handleUpdate = (isLiked, isCompleted) => {
    dispatch(
      sendClientAnalytics(
        isLiked !== liked
          ? createClientAnalytics(
              AuditEventAction.FAV_RESOURCE.category,
              AuditEventAction.FAV_RESOURCE.action,
              {
                resource: data.title,
                liked: isLiked,
              },
            )
          : createClientAnalytics(
              AuditEventAction.COMPLETE_RESOURCE.category,
              AuditEventAction.COMPLETE_RESOURCE.action,
              {
                resource: data.title,
                completed: isCompleted,
              },
            ),
      ),
    );
    setLiked(isLiked);
    setCompleted(isCompleted);
    dispatch(
      sendUpdateResourceActivity({
        resourceId: id,
        isLiked: isLiked,
        isCompleted: isCompleted,
        isRecommended: data.isRecommended,
      }),
    );
  };

  const iconStyle = {
    width: '1.25rem',
    height: '1.25rem',
    color: COLOR_BLACK_PERL,
  };

  const iconLabelBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const iconLabelStyle = {
    mt: '0.5rem',
    fontSize: FONT_SIZE_13_PX,
    color: COLOR_BLACK_PERL,
    textAlign: 'center',
    lineHeight: 'normal',
  };

  const onResourceLinkClick = () => {
    dispatch(
      sendClientAnalytics(
        createClientAnalytics(
          AuditEventAction.LAUNCH_RESOURCE.category,
          AuditEventAction.LAUNCH_RESOURCE.action,
          {
            resource: data.title,
          },
        ),
      ),
    );
  };

  return (
    <Card
      sx={{
        width: { xs: '20.5rem', sm: '28.5rem', md: '35rem' },
        height: { xs: '41.8125rem', sm: '44.5rem', md: '47.25rem', xl: '48rem' },
        borderRadius: '0.75rem',
        background: color,
        mb: '2rem',
        [theme.breakpoints.down('lg')]: {
          mr: '0.75rem',
        },
      }}
    >
      <CardActionArea href={data.link} target="_blank" onClick={onResourceLinkClick}>
        <CardMedia
          sx={{ height: { xs: '11.5625rem', md: '13.125rem', lg: '16.0625rem' } }}
          image={headerImage}
          title={data.title}
        />
      </CardActionArea>
      <CardContent
        sx={{
          height: '54%',
          [theme.breakpoints.down('lg')]: { height: '60%', overflow: 'scroll' },
        }}
      >
        <Typography
          gutterBottom
          variant="h4"
          color={COLOR_BLACK_PERL}
          sx={{
            padding: '0.3rem',
            [theme.breakpoints.down('lg')]: {
              fontSize: '1.25rem',
            },
          }}
        >
          {data.title}
        </Typography>
        <Grid container direction="row" wrap="nowrap" sx={{ mt: '1.5rem', mb: '2rem' }}>
          <Grid item xs={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                [theme.breakpoints.up('lg')]: {
                  ml: '5rem',
                },
              }}
              alignItems="center"
            >
              {selectIconByCategory(data.symptom, iconStyle)}
              <Typography variant="subtitle1" sx={iconLabelStyle}>
                {getCategoryDisplayLabel(intl, data.symptom, '')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={iconLabelBoxStyle} alignItems="center">
              {selectIconByMediaType(data.mediaType)}
              <Typography variant="subtitle1" sx={iconLabelStyle}>
                {data.mediaType}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                [theme.breakpoints.up('lg')]: {
                  mr: '5.5rem',
                },
              }}
              alignItems="center"
            >
              {getTimeIcon()}
              <Typography variant="subtitle1" sx={iconLabelStyle}>
                {data.duration
                  ? data.duration
                      .replace(/\b(?:Less than 15 mins|Moins de 15 minutes)\b/, '< 15 min')
                      .replace(/\b(?:More than 15 mins|Plus de 15 minutes)\b/, '> 15 min')
                  : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Typography
          variant="h5"
          color={COLOR_BLACK_PERL}
          sx={{
            mt: '2rem',
            ml: '1.5rem',
          }}
        >
          {intl.formatMessage(messages.resourceCardExpandedTaglineTitle)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            padding: '0.5rem',
            color: COLOR_BLACK_PERL,
          }}
        >
          {data.tagLine}
        </Typography>
        <Typography
          variant="h5"
          color={COLOR_BLACK_PERL}
          sx={{
            mt: '1.5rem',
            ml: '1.5rem',
            [theme.breakpoints.down('lg')]: {
              mt: '1rem',
            },
          }}
        >
          {intl.formatMessage(messages.resourceCardExpandedDescriptionTitle)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            padding: '0.5rem',
            color: COLOR_BLACK_PERL,
          }}
        >
          {data.description}
        </Typography>
      </CardContent>
      <Divider variant="middle" sx={{ bgcolor: COLOR_BLACK_PERL }} />
      <CardActions sx={{ height: '5%' }}>
        <IconButton
          data-testid="like-button"
          size="small"
          onClick={() => {
            handleUpdate(!liked, completed);
          }}
          sx={{
            ml: '0.5rem',
            bgcolor: color,
            ':hover': {
              bgcolor: color,
            },
          }}
        >
          {liked ? <Favorite sx={iconStyle} /> : <FavoriteBorder sx={iconStyle} />}
          <Typography variant="subtitle1" sx={{ ml: '0.4rem', color: COLOR_BLACK_PERL }}>
            {liked
              ? intl.formatMessage(messages.resourceCardExpandedLikedLabel)
              : intl.formatMessage(messages.resourceCardExpandedLikeLabel)}
          </Typography>
        </IconButton>
        <FormControlLabel
          value="start"
          control={
            <Checkbox
              data-testid="complete-checkbox"
              checked={completed}
              onChange={() => {
                handleUpdate(liked, !completed);
              }}
              sx={{
                transform: 'scale(0.8)',
                color: COLOR_BLACK_PERL,
                '&.Mui-checked': {
                  color: COLOR_BLACK_PERL,
                },
              }}
            />
          }
          label={
            <Typography variant="subtitle1" sx={{ color: COLOR_BLACK_PERL }}>
              {completed
                ? intl.formatMessage(messages.resourceCardExpandedCompletedLabel)
                : intl.formatMessage(messages.resourceCardExpandedCompleteLabel)}
            </Typography>
          }
          labelPlacement="start"
          sx={{
            ml: 'auto',
            mr: '0.2rem',
          }}
        />
      </CardActions>
    </Card>
  );
};
